import { useEffect, useRef, useState, type ChangeEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { Area } from 'react-easy-crop';
import { ImagePlus, Loader2, X } from 'lucide-react';
import { addDoc, collection } from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';
import { LazyCropper } from './LazyCropper';
import { FIELD } from './UI';
import { db, storage } from '../services/firebase';
import { notifyUsers } from '../services/notificationService';
import { userMessage } from '../utils/userMessage';
import type { Memory, TripConfig, User } from '../types';

async function preparePhoto(src: string, crop: Area): Promise<Blob> {
  const image = new Image();
  image.src = src;
  await image.decode();
  const scale = Math.min(1, 1600 / Math.max(crop.width, crop.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(crop.width * scale));
  canvas.height = Math.max(1, Math.round(crop.height * scale));
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Could not process this photo.');
  context.drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, canvas.width, canvas.height);
  for (const quality of [0.78, 0.62, 0.48]) {
    const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/jpeg', quality));
    if (blob && blob.size <= 2 * 1024 * 1024) return blob;
  }
  throw new Error('This photo is still too large. Try a tighter crop.');
}

export function MemoryComposer({ tripConfig, user, children, variant = 'compact' }: {
  tripConfig: TripConfig; user: User; children: ReactNode; variant?: 'empty' | 'compact';
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const [src, setSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState<Area | null>(null);
  const [aspect, setAspect] = useState(4 / 3);
  const [originalAspect, setOriginalAspect] = useState(4 / 3);
  const [caption, setCaption] = useState('');
  const [status, setStatus] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => () => { if (src) URL.revokeObjectURL(src); }, [src]);
  useEffect(() => {
    if (!src) return;
    dialogRef.current?.querySelector<HTMLButtonElement>('button[aria-label="Close"]')?.focus();
    return () => { triggerRef.current?.focus(); };
  }, [src]);
  useEffect(() => {
    if (!src) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !saving) setSrc(null);
      if (event.key !== 'Tab') return;
      const items = dialogRef.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled)');
      if (!items?.length) return;
      const first = items[0], last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [src, saving]);

  const selectPhoto = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Choose a photo to add as a memory.');
      return;
    }
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      setOriginalAspect(image.naturalWidth / image.naturalHeight);
      setAspect(image.naturalWidth / image.naturalHeight);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setArea(null);
      setCaption('');
      setSrc(url);
    };
    image.onerror = () => { URL.revokeObjectURL(url); alert('Could not open this photo.'); };
    image.src = url;
  };

  const save = async () => {
    if (!src || !area || saving) return;
    setSaving(true);
    let photoRef: ReturnType<typeof ref> | null = null;
    try {
      setStatus('Preparing photo…');
      const blob = await preparePhoto(src, area);
      photoRef = ref(storage, `trips/${tripConfig.id}/memories/${crypto.randomUUID()}.jpg`);
      setStatus('Uploading…');
      const task = uploadBytesResumable(photoRef, blob, { contentType: 'image/jpeg' });
      await new Promise<void>((resolve, reject) => task.on('state_changed',
        snapshot => setStatus(`Uploading ${Math.round(snapshot.bytesTransferred / snapshot.totalBytes * 100)}%…`),
        reject, resolve));
      const imageUrl = await getDownloadURL(task.snapshot.ref);
      const newMemory: Memory = {
        id: `mem-${Date.now()}`, imageUrl, caption: caption.trim().slice(0, 50), by: user.id,
        date: new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }),
        timestamp: Date.now(),
      };
      const docRef = await addDoc(collection(db, 'trips', tripConfig.id, 'memories'), newMemory);
      setSrc(null);
      setStatus('');
      await notifyUsers({
        tripId: tripConfig.id, tripTitle: tripConfig.title,
        recipientIds: tripConfig.memberIds || [], senderId: user.id, senderName: user.name,
        title: 'New memory posted',
        message: newMemory.caption
          ? `${user.name.split(' ')[0]} posted a memory — **${newMemory.caption}**`
          : `${user.name.split(' ')[0]} posted a **new memory**`,
        priority: 'P1', category: 'social', emoji: '📸', type: 'social',
        deepLink: { tab: 'memories', itemId: docRef.id },
      }).catch(error => console.error('Memory posted, but member notification failed:', error));
    } catch (error) {
      if (photoRef) await deleteObject(photoRef).catch(() => {});
      console.error('Memory upload failed:', error);
      alert(userMessage(error, 'Could not upload this memory. Check your connection and try again.'));
      setStatus('');
    } finally {
      setSaving(false);
    }
  };

  return <>
    <button ref={triggerRef} type="button" onClick={() => inputRef.current?.click()}
      className={`w-full flex flex-col items-center justify-center gap-2 p-4 rounded-2xl border-2 border-dashed border-app-border/50 bg-app-faint/70 text-app-sub/60 transition-all active:scale-[0.98] hover:border-app-sub/40 hover:text-app-sub ${variant === 'empty' ? 'min-h-[112px]' : 'min-h-[80px]'}`}>
      <span className="w-7 h-7 rounded-full flex items-center justify-center bg-app-sub text-white shadow-lg shadow-app-sub/20" aria-hidden="true">
        <ImagePlus className="w-4 h-4" />
      </span>
      <span className="text-[11px] font-black uppercase tracking-widest">{children}</span>
    </button>
    <input ref={inputRef} type="file" accept="image/*" className="sr-only" tabIndex={-1} aria-label="Choose a photo" onChange={selectPhoto} />
    {src && createPortal(
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-label="Add memory" className="fixed inset-0 z-[200] bg-app-bg flex flex-col h-[100dvh]">
        <div className="flex items-center justify-between p-4 border-b border-app-border">
          <h2 className="font-serif text-2xl font-bold text-app-text">Add memory</h2>
          <button type="button" aria-label="Close" disabled={saving} onClick={() => setSrc(null)} className="p-2 text-app-sub"><X className="w-5 h-5" /></button>
        </div>
        <div className="relative flex-1 min-h-0 bg-black"><LazyCropper image={src} crop={crop} zoom={zoom} aspect={aspect} onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={(_, pixels) => setArea(pixels)} /></div>
        <div className="p-4 pb-[max(1rem,env(safe-area-inset-bottom))] space-y-3 bg-app-bg">
          <div className="flex gap-2" aria-label="Crop shape">
            {([['Original', originalAspect], ['Square', 1], ['Portrait', 4 / 5]] as const).map(([label, value]) => (
              <button key={label} type="button" aria-pressed={aspect === value} onClick={() => { setArea(null); setAspect(value); }}
                className={`rounded-full px-3 py-1.5 text-sm ${aspect === value ? 'bg-gold text-white' : 'bg-app-faint text-app-sub'}`}>{label}</button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <label htmlFor="memory-zoom" className="text-sm text-app-sub">Zoom</label>
            <input id="memory-zoom" type="range" min="1" max="3" step="0.01" value={zoom} onChange={e => setZoom(Number(e.target.value))} className="flex-1 accent-gold" />
          </div>
          <label htmlFor="memory-caption" className="block text-sm font-semibold text-app-text">Caption <span className="font-normal text-app-sub">(optional)</span></label>
          <input id="memory-caption" className={FIELD} maxLength={50} value={caption} onChange={e => setCaption(e.target.value)} placeholder="A moment to remember…" />
          <button type="button" disabled={!area || saving} onClick={save} className="w-full rounded-xl bg-gold px-4 py-3 text-sm font-semibold text-white disabled:opacity-40 flex items-center justify-center gap-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}{saving ? status : 'Save memory'}
          </button>
        </div>
      </div>, document.getElementById('import-modal-root') ?? document.body)
    }
  </>;
}
